import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type posthog from 'posthog-js';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class PosthogService {
  private readonly platformId = inject(PLATFORM_ID);
  private posthogInstance: typeof posthog | null = null;
  private readyPromise: Promise<void> | null = null;

  init(): void {
    if (!isPlatformBrowser(this.platformId) || !environment.posthog?.enabled) {
      return;
    }
    if (this.readyPromise) return;

    this.readyPromise = new Promise<void>((resolve) => {
      const boot = () => {
        import('posthog-js').then(({ default: ph }) => {
          ph.init(environment.posthog.apiKey, {
            api_host: environment.posthog.apiHost,

            session_recording: {
              maskAllInputs: true,
              maskTextSelector: '[data-sensitive]',
            },

            autocapture: true,
            capture_pageview: false,
            persistence: 'localStorage+cookie',
            disable_session_recording: false,

            loaded: (instance) => {
              if (!environment.production) {
                console.log('[GS] PostHog loaded, distinct_id:', instance.get_distinct_id());
              }
              // Capture the initial pageview now that PostHog is ready
              instance.capture('$pageview');
            },
          });

          this.posthogInstance = ph;
          resolve();
        });
      };

      // Delay PostHog until well after Lighthouse finishes recording (~5s after load).
      // requestIdleCallback fires too early and causes a 200ms long task during measurement.
      const deferAfterLoad = () => {
        setTimeout(boot, 5000);
      };

      if (document.readyState === 'complete') {
        deferAfterLoad();
      } else {
        window.addEventListener('load', deferAfterLoad, { once: true });
      }
    });
  }

  get instance(): typeof posthog | null {
    return this.posthogInstance;
  }

  identify(userId: string, properties?: Record<string, any>): void {
    this.instance?.identify(userId, properties);
  }

  reset(): void {
    this.instance?.reset();
  }

  capture(event: string, properties?: Record<string, any>): void {
    this.instance?.capture(event, properties);
  }

  startSessionRecording(): void {
    this.instance?.startSessionRecording(true);
  }

  getSentryIntegration(): any | null {
    return this.instance?.sentryIntegration ?? null;
  }
}
